import { ReactNode } from 'react';
import classNames from 'classnames';
import css from './Table.module.scss';
import { BaseTable } from '../BaseTable/BaseTable';
import { BaseTableBody } from '../BaseTableBody/BaseTableBody';
import { BaseTableCell } from '../BaseTableCell/BaseTableCell';
import { BaseTableHead } from '../BaseTableHead/BaseTableHead';
import { BaseTableRow } from '../BaseTableRow/BaseTableRow';
import { TableProps } from './Table.types';

/**
 * This control requires a numeric length passed in as a generic to indicate how many columns the
 * table will have and how long each array must be. It then uses the data to construct a table by mapping
 * from the `headers` and `rows` arrays.
 *
 * @param tableKey - {@link TableProps.tableKey tableKey} description.
 * @param cellWidths - {@link TableProps.cellWidths cellWidths} description.
 * @param headers - {@link TableProps.headers headers} description.
 * @param rows - {@link TableProps.rows rows} description.
 */
const Table = <Length extends number>({
    tableKey,
    cellWidths,
    headers,
    rows,
}: TableProps<Length>) => {
    return (
        <BaseTable>
            <BaseTableHead>
                <BaseTableRow
                    key={`${tableKey}-header`}
                    classes={{
                        root: css.headerRow,
                    }}
                >
                    {headers.map((header: string, headerIndex: number) => {
                        return (
                            <BaseTableCell
                                key={`${tableKey}-header-cell-${headerIndex}`}
                                classes={{
                                    root: css.headerCell,
                                }}
                                sx={{
                                    width: `${cellWidths[headerIndex]}%`,
                                }}
                            >
                                {header}
                            </BaseTableCell>
                        );
                    })}
                </BaseTableRow>
            </BaseTableHead>
            <BaseTableBody>
                {rows.map((row: ReactNode[], rowIndex: number) => {
                    return (
                        <BaseTableRow
                            key={`${tableKey}-body-row-${rowIndex}`}
                            classes={{
                                root: classNames(css.bodyRow, { [css.alternate]: rowIndex % 2 !== 0 }),
                                hover: css.hover,
                            }}
                        >
                            {row.map((cell: ReactNode, cellIndex: number) => {
                                return (
                                    <BaseTableCell
                                        key={`${tableKey}-body-row-${rowIndex}-cell-${cellIndex}`}
                                        classes={{
                                            root: css.bodyCell,
                                        }}
                                        sx={{
                                            width: `${cellWidths[cellIndex]}%`,
                                        }}
                                    >
                                        {cell}
                                    </BaseTableCell>
                                );
                            })}
                        </BaseTableRow>
                    );
                })}
            </BaseTableBody>
        </BaseTable>
    );
};

export default Table;